import Configuration from '@/api/Configuration';
import { HttpStatus } from '@/api/HttpStatus';
import { toImage } from '@/api/mappers/mapImage';
import {
  toApiNetContent,
  toApiSkuCreateRequest,
  toApiSkuUpdateRequest,
  toProductSearchResult,
  toSku,
  toSkus,
} from '@/api/mappers/mapSku';
import { toApiStatus } from '@/api/mappers/mapStatus';
import Settings from '@/api/Settings';
import { toStringArray } from '@/api/utils';
import {
  Status as ApiStatus,
  BulkUpdateSkusRequest,
  CreateSkuRequest,
  DeleteSkuImageRequest,
  DeleteSkuRequest,
  GetSkuMetricsRequest,
  GetSkuRequest,
  ListSkusRequest,
  SearchSkusFacetCountModeEnum,
  SearchSkusRequest,
  SKUsApi,
  UpdateSkuRequest,
  UploadSkuImageRequest,
} from '@/gen/api';
import { Image } from '@/models/Image';
import {
  ProductSearchResult,
  Sku,
  SkuAssociation,
  SkuCreate,
  SkuMetrics,
  Skus,
  SkuUpdate,
} from '@/models/Sku';
import { Status } from '@/models/Status';

export interface UploadProgress {
  uploadedSize: number;
  totalSize: number;
  percentage: number;
}

export type ProgressCallback = (progress: UploadProgress) => void;

export interface SkuFilters {
  supplierId: string | string[];
  productId: string | string[];
  skuId: string | string[];
  storeId: string;
  productCategoryId: string | string[];
  productCategoryName: string | string[];
  productListId: string | string[];
  promotions: boolean;
  status: Status;
  createdByStoreId: string;
}

export interface SkuSort {
  sortBy: 'skuName' | 'categoryName';
  sortDirection: 'asc' | 'desc';
}

export type ProductSearchFacetCountMode = 'total' | 'window' | 'available' | 'none';

const ONE_MB = 1024 * 1024;

interface ImageUploadProgressResponse {
  upload_id: string;
}

interface SkusApi {
  listSkus(
    offset?: number,
    limit?: number,
    sort?: SkuSort[],
    include?: SkuAssociation[],
  ): Promise<Skus>;
  findSkus(
    query?: string,
    filters?: Partial<SkuFilters>,
    offset?: number,
    limit?: number,
    sort?: SkuSort[],
    include?: SkuAssociation[],
  ): Promise<Skus>;
  searchSkus(
    query?: string,
    filters?: Partial<SkuFilters>,
    offset?: number,
    limit?: number,
    sort?: SkuSort[],
    include?: SkuAssociation[],
    facetCountMode?: ProductSearchFacetCountMode,
  ): Promise<ProductSearchResult>;
  getSkuMetrics(filters?: Partial<SkuFilters>): Promise<SkuMetrics>;
  createSku(sku: SkuCreate): Promise<Sku | undefined>;
  deleteSku(id: string): Promise<void>;
  getSku(id: string, include?: SkuAssociation[]): Promise<Sku | undefined>;
  getSkus(ids: string[], include?: SkuAssociation[]): Promise<Sku[]>;
  updateSku(id: string, sku: SkuUpdate): Promise<Sku | undefined>;
  bulkUpdateSkus(supplierId: string, skus: (SkuCreate | SkuUpdate)[] | File): Promise<void>;
  uploadSkuImage(
    id: string,
    file: File,
    number: number,
    progressCallback: ProgressCallback,
  ): Promise<Image>;
  deleteSkuImage(id: string, number: number): Promise<void>;
}

export class SkusApiImpl implements SkusApi {
  private configuration: Configuration;
  private skusApi: SKUsApi;
  private settings: Settings | undefined;

  constructor(configuration: Configuration, settings?: Settings) {
    this.configuration = configuration;
    this.skusApi = new SKUsApi(configuration);
    this.settings = settings;
  }

  async listSkus(
    offset?: number,
    limit?: number,
    sort?: SkuSort[],
    include?: SkuAssociation[],
  ): Promise<Skus> {
    const requestParams: ListSkusRequest = {
      offset,
      limit,
      include: include?.map((value) => value.toString()),
      currencyCode: this.settings?.currencyCode,
      languageCode: this.settings?.languageCode,
      sortBy: sort?.map((value) => value.sortBy),
      sortDirection: sort?.map((value) => value.sortDirection),
    };

    try {
      const response = await this.skusApi.listSkus(requestParams);
      return toSkus(response);
    } catch (error) {
      console.error('Error listing SKUs:', error);
      throw error;
    }
  }

  async findSkus(
    query?: string,
    filters?: Partial<SkuFilters>,
    offset?: number,
    limit?: number,
    sort?: SkuSort[],
    include?: SkuAssociation[],
  ): Promise<Skus> {
    const requestParams: ListSkusRequest = {
      query,
      offset,
      limit,
      include: include?.map((value) => value.toString()),
      currencyCode: this.settings?.currencyCode,
      languageCode: this.settings?.languageCode,
      sortBy: sort?.map((value) => value.sortBy),
      sortDirection: sort?.map((value) => value.sortDirection),
      supplierId: toStringArray(filters?.supplierId),
      productId: toStringArray(filters?.productId),
      skuId: toStringArray(filters?.skuId),
      productCategoryId: toStringArray(filters?.productCategoryId),
      productCategoryName: toStringArray(filters?.productCategoryName),
      createdByStoreId: filters?.createdByStoreId,
      storeId: filters?.storeId,
      promotions: filters?.promotions,
      status: filters?.status,
    };

    try {
      const response = await this.skusApi.listSkus(requestParams);
      return toSkus(response);
    } catch (error) {
      console.error('Error finding SKUs:', error);
      throw error;
    }
  }

  async searchSkus(
    query?: string,
    filters?: Partial<SkuFilters>,
    offset?: number,
    limit?: number,
    sort?: SkuSort[],
    include?: SkuAssociation[],
    facetCountMode: ProductSearchFacetCountMode = 'window',
  ): Promise<ProductSearchResult> {
    const requestParams: SearchSkusRequest = {
      query,
      offset,
      limit,
      include: include?.map((value) => value.toString()),
      currencyCode: this.settings?.currencyCode,
      languageCode: this.settings?.languageCode,
      sortBy: sort?.map((value) => value.sortBy),
      sortDirection: sort?.map((value) => value.sortDirection),
      facetCountMode: facetCountMode as SearchSkusFacetCountModeEnum,
      supplierId: toStringArray(filters?.supplierId),
      productId: toStringArray(filters?.productId),
      skuId: toStringArray(filters?.skuId),
      productCategoryId: toStringArray(filters?.productCategoryId),
      productCategoryName: toStringArray(filters?.productCategoryName),
      createdByStoreId: filters?.createdByStoreId,
      storeId: filters?.storeId,
      promotions: filters?.promotions,
      status: filters?.status,
    };

    try {
      const response = await this.skusApi.searchSkus(requestParams);
      return toProductSearchResult(response);
    } catch (error) {
      console.error('Error searching SKUs:', error);
      throw error;
    }
  }

  async getSkuMetrics(filters?: Partial<SkuFilters>): Promise<SkuMetrics> {
    const requestParams: GetSkuMetricsRequest = {
      supplierId: toStringArray(filters?.supplierId),
      productId: toStringArray(filters?.productId),
      skuId: toStringArray(filters?.skuId),
      productCategoryId: toStringArray(filters?.productCategoryId),
      productCategoryName: toStringArray(filters?.productCategoryName),
      createdByStoreId: filters?.createdByStoreId,
      storeId: filters?.storeId,
      promotions: filters?.promotions,
      status: filters?.status,
    };

    try {
      const response = await this.skusApi.getSkuMetrics(requestParams);
      return {
        skuCount: response.skuCount,
      };
    } catch (error) {
      console.error('Error getting SKU metrics:', error);
      throw error;
    }
  }

  async getSku(id: string, include?: SkuAssociation[]): Promise<Sku | undefined> {
    const requestParams: GetSkuRequest = {
      id: id.toString(),
      include: include?.map((value) => value.toString()),
      currencyCode: this.settings?.currencyCode,
      languageCode: this.settings?.languageCode,
    };

    try {
      const response = await this.skusApi.getSku(requestParams);
      return toSku(response);
    } catch (error) {
      console.error('Error getting SKU:', error);
      return;
    }
  }

  async getSkus(ids: string[], include?: SkuAssociation[]): Promise<Sku[]> {
    if (ids.length === 0) {
      return [];
    }

    const requestParams: ListSkusRequest = {
      include: include?.map((value) => value.toString()),
      limit: ids.length,
      skuId: ids,
      currencyCode: this.settings?.currencyCode,
      languageCode: this.settings?.languageCode,
    };

    try {
      const response = await this.skusApi.listSkus(requestParams);
      return response.items.map(toSku);
    } catch (error) {
      console.error('Error listing SKUs:', error);
      return [];
    }
  }

  async createSku(sku: SkuCreate): Promise<Sku | undefined> {
    const requestParams: CreateSkuRequest = {
      skuCreateRequest: toApiSkuCreateRequest(sku),
    };

    try {
      const response = await this.skusApi.createSku(requestParams);
      return toSku(response);
    } catch (error) {
      console.error('Error creating SKU:', error);
      return undefined;
    }
  }

  async updateSku(id: string, sku: SkuUpdate): Promise<Sku | undefined> {
    const requestParams: UpdateSkuRequest = {
      id,
      skuUpdateRequest: toApiSkuUpdateRequest(sku),
    };

    try {
      const response = await this.skusApi.updateSku(requestParams);
      return toSku(response);
    } catch (error) {
      console.error('Error updating SKU:', error);
      return undefined;
    }
  }

  async deleteSku(id: string): Promise<void> {
    const requestParams: DeleteSkuRequest = {
      id,
    };

    try {
      await this.skusApi.deleteSku(requestParams);
    } catch (error) {
      console.error('Error deleting SKU:', error);
      return;
    }
  }

  async uploadSkuImage(
    id: string,
    file: File,
    number: number,
    onProgress: ProgressCallback,
  ): Promise<Image> {
    if (file.size > 2 * ONE_MB) {
      return await this.uploadSkuImageInChunks(id, file, number, onProgress);
    }

    const requestParams: UploadSkuImageRequest = {
      id,
      file,
      imageNumber: number,
      contentType: file.type,
    };

    try {
      const image = await this.skusApi.uploadSkuImage(requestParams);

      onProgress({
        uploadedSize: file.size,
        totalSize: file.size,
        percentage: 100,
      });

      return toImage(image);
    } catch (error) {
      console.error('Error deleting SKU:', error);
      throw error;
    }
  }

  private async uploadSkuImageInChunks(
    id: string,
    file: File,
    number: number,
    onProgress?: ProgressCallback,
  ): Promise<Image> {
    const chunkSize = ONE_MB;
    const totalSize = file.size;
    const totalChunks = Math.ceil(totalSize / chunkSize);

    let uploadId: string | undefined = undefined;
    let uploadedSize = 0;

    for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
      const start = chunkNumber * chunkSize;
      const end = Math.min(start + chunkSize, totalSize);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('file', chunk, file.name);
      formData.append('chunk_number', chunkNumber.toString());
      formData.append('total_chunks', totalChunks.toString());
      formData.append('chunk_size', chunkSize.toString());
      formData.append('total_size', totalSize.toString());
      formData.append('filename', file.name);
      formData.append('content_type', file.type);
      if (uploadId) {
        formData.append('upload_id', uploadId);
      }

      const response = await fetch(
        `${this.configuration.basePath}/skus/${id}/images/upload/${number}`,
        {
          method: 'POST',
          headers: { ...this.configuration.headers },
          body: formData,
        },
      );

      if (response.status === HttpStatus.OK) {
        const image = (await response.json()) as Image;
        uploadedSize = totalSize;
        onProgress?.({
          uploadedSize,
          totalSize,
          percentage: 100,
        });
        return image;
      }

      if (response.status === HttpStatus.Accepted) {
        const progress = (await response.json()) as ImageUploadProgressResponse;
        if (!uploadId && progress.upload_id) {
          uploadId = progress.upload_id;
        }

        uploadedSize = end;
        const percentage = Math.min((uploadedSize / totalSize) * 100, 99);
        onProgress?.({
          uploadedSize,
          totalSize,
          percentage,
        });
        continue;
      }

      const errorText = await response.text();
      throw new Error(`Unexpected response: ${response.status} ${errorText}`);
    }

    throw new Error('Unexpected termination before final chunk upload');
  }

  async deleteSkuImage(id: string, number: number): Promise<void> {
    const requestParams: DeleteSkuImageRequest = {
      id,
      number,
    };

    try {
      await this.skusApi.deleteSkuImage(requestParams);
    } catch (error) {
      console.error('Error deleting SKU image:', error);
      return;
    }
  }

  async bulkUpdateSkus(supplierId: string, skus: (SkuCreate | SkuUpdate)[] | File): Promise<void> {
    if (skus instanceof File) {
      const requestParams = {
        supplierId,
        bulkSkuUpdateRequest: {
          supplierId,
          skus: [],
        },
      };

      const contentTypeMap: Record<string, string> = {
        'text/csv': 'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      };

      const contentType = contentTypeMap[skus.type] || '';

      try {
        await this.skusApi.bulkUpdateSkus(requestParams, {
          body: skus,
          headers: {
            ...this.configuration.headers,
            'Content-Type': contentType,
          },
        });
      } catch (error) {
        console.error('Error uploading file for SKU bulk update:', error);
      }
      return;
    }

    const requestParams: BulkUpdateSkusRequest = {
      bulkSkuUpdateRequest: {
        supplierId,
        skus: skus.map((sku) => ({
          id: '',
          ...sku,
          status: sku.status ? toApiStatus(sku.status) : ApiStatus.Active,
          product: sku.product
            ? {
                ...sku.product,
                status: sku.product.status ? toApiStatus(sku.product.status) : ApiStatus.Active,
              }
            : undefined,
          images: undefined,
          netContent: sku.netContent ? toApiNetContent(sku.netContent) : undefined,
        })),
      },
      supplierId,
    };

    try {
      await this.skusApi.bulkUpdateSkus(requestParams);
    } catch (error) {
      console.error('Error updating SKUs:', error);
    }
  }
}

export default SkusApi;
